import { useMemo } from "react";
import { useTranslation } from "@sagot/i18n";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { serviceKeys } from "../../../service/service-keys";
import { useServiceQuery } from "../../../service/use-service-query";
import type { ServiceFieldOption, ServiceRefs } from "./service-refs";

const CONFIG_STALE_MS = 5 * 60_000;

interface Labelled {
  en: string;
  fil?: string | null;
}

/** A label in the console's language; English when the translation is missing. */
export function labelIn(label: Labelled, language: string): string {
  const wanted = language === "fil" || language.startsWith("fil-") ? label.fil : undefined;
  return wanted && wanted.trim() !== "" ? wanted : label.en;
}

/**
 * The lists the Service Desk step forms pick from: ticket types, statuses, resolution codes, the ticket SLA policies and the
 * custom fields of the tickets. Read only when the Service Desk can be used (`enabled`), so a workspace without it makes no
 * calls; each list shares the cache of the Service screens that read the same thing. A list that cannot be read stays empty.
 */
export function useServiceRefs(enabled: boolean): ServiceRefs {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const ws = useWorkspaceId();
  const types = useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, "types-active"),
    enabled,
    staleTime: CONFIG_STALE_MS,
    request: (api, signal) => api.GET("/v1/ticket-types", { params: { query: { status: "active" } }, signal }),
  });
  const statuses = useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, "statuses"),
    enabled,
    staleTime: CONFIG_STALE_MS,
    request: (api, signal) => api.GET("/v1/ticket-statuses", { signal }),
  });
  const codes = useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, "resolution-codes"),
    enabled,
    staleTime: CONFIG_STALE_MS,
    request: (api, signal) => api.GET("/v1/resolution-codes", { params: { query: { active: true } }, signal }),
  });
  const policies = useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, "workflow-sla-policies"),
    enabled,
    staleTime: CONFIG_STALE_MS,
    request: (api, signal) => api.GET("/v1/sla-policies", { params: { query: { scope: "ticket" } }, signal }),
  });
  const fields = useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, "workflow-field-definitions"),
    enabled,
    staleTime: CONFIG_STALE_MS,
    request: (api, signal) => api.GET("/v1/ticket-field-definitions", { signal }),
  });
  return useMemo<ServiceRefs>(
    () => ({
      types: (types.data ?? []).map((type) => ({ id: type.id, name: labelIn(type.name, language), key: type.system_key, category: type.category })),
      statuses: (statuses.data ?? []).filter((status) => status.selectable).map((status) => ({ id: status.key, name: labelIn(status.staff_label, language), category: status.category })),
      codes: (codes.data ?? []).filter((code) => code.active).map((code) => ({ id: code.key, name: labelIn(code.label, language), categories: code.allowed_categories })),
      policies: (policies.data ?? []).map((policy) => ({ id: policy.id, name: policy.name })),
      // Ticket fields, and the attributes of organisations and contacts a condition can read; one that holds personal data (pii) is never offered as a condition.
      fields: (fields.data ?? []).flatMap((field): ServiceFieldOption[] => {
        const object = field.object === "organisation" || field.object === "contact" ? field.object : "ticket";
        return object !== "ticket" && field.pii ? [] : [{ key: field.key, name: labelIn(field.label, language), kind: field.type, object }];
      }),
      loading: enabled && (types.isPending || statuses.isPending),
    }),
    [types.data, types.isPending, statuses.data, statuses.isPending, codes.data, policies.data, fields.data, language, enabled],
  );
}
